import { type ChainId, ChainUtils, type MarketId } from '@morpho-org/blue-sdk'
import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { type InterfaceInputToken, parseApiAsset } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'
import { formatters } from '@morpho-org/ui-v6/utils'
import { formatUnits } from 'viem'

import type { Warning } from '@/components/common/WarningIcon/WarningHoverCard/WarningHoverCardContent'

import {
  MARKET_TRANSACTION_TYPE_LABELS,
  MARKET_TRANSACTION_TYPE_TO_LEGACY
} from '@/components/MarketPage/MarketActivitySection/MarketTransactions/config'
import { getParsedValue } from '@/helpers/formatters'
import { toInterfaceInputToken } from '@/helpers/interfaceToken'
import { getMarketWarningMessage, MarketWarningType } from '@/helpers/warnings'

import type { UserBorrowTransaction } from '../borrowTransactions.contract'
import type { UserEarnTransaction } from '../earnTransactions.contract'
import type { UserLiquidation } from '../liquidations.contract'

import {
  type BorrowActivityTableRow,
  type EarnActivityTableRow,
  EarnTransactionType,
  type LiquidationActivityTableRow
} from '../config/DashboardActivity.types'
import { toWarningLevel } from './activityWarningLevel'
import { mapEarnActivityWarnings } from './earnActivityWarnings'

// Lives outside the hooks so it can be unit-tested: importing a hook pulls in
// the config chain, which validates env at import time.

type MarketWarning = NonNullable<UserBorrowTransaction['market']>['warnings'][number]

interface LoanAssetLabel {
  symbol: string | null
  decimals: number
}

const COLLATERAL_TYPES = new Set<UserBorrowTransaction['type']>([
  'supplyCollateral',
  'withdrawCollateral'
])

const EARN_TYPES: Record<UserEarnTransaction['type'], EarnTransactionType> = {
  deposit: EarnTransactionType.Deposit,
  withdraw: EarnTransactionType.Withdraw,
  transfer: EarnTransactionType.Transfer
}

/**
 * Core sends each row's USD value but no token price. The amount cell needs a price to pick
 * how many decimals a small amount shows, so it is read back from the row's own USD value.
 */
function withRowPrice(
  token: InterfaceInputToken,
  assets: string | null,
  assetsUsd: number | null
): InterfaceInputToken {
  if (assets === null || assetsUsd === null) return token
  const amount = Number(formatUnits(BigInt(assets), token.decimals))
  if (!amount) return token
  return { ...token, price: { usd: assetsUsd / amount } }
}

function unrealizedBadDebtMessage(
  loanAsset: LoanAssetLabel,
  badDebtAssets: string | null,
  badDebtUsd: number | null
): string {
  const formattedBadDebt = formatters.short
    .unit(loanAsset.symbol ?? undefined)
    .digits(2)
    .of(BigInt(badDebtAssets ?? 0), loanAsset.decimals)
  const formattedBadDebtUsd = formatters.short
    .unit('$')
    .digits(2)
    .of(safeParseNumber(badDebtUsd ?? 0), 18)
  return `Unrealized bad debt: ${formattedBadDebt} (${formattedBadDebtUsd})`
}

function marketWarningMessage(
  warning: MarketWarning,
  loanAsset: LoanAssetLabel
): string | undefined {
  if (warning.type === MarketWarningType.CUSTOM && warning.content) return warning.content
  if (warning.type === MarketWarningType.BAD_DEBT_UNREALIZED) {
    return unrealizedBadDebtMessage(loanAsset, warning.badDebtAssets, warning.badDebtUsd)
  }
  // The other types read only `type` and the loan asset.
  return (
    getMarketWarningMessage({
      // oxlint-disable-next-line typescript/consistent-type-assertions
      warning: warning as never,
      loanAsset: { symbol: loanAsset.symbol ?? undefined, decimals: loanAsset.decimals }
    }) ?? undefined
  )
}

export function mapMarketActivityWarnings(
  warnings: readonly MarketWarning[],
  loanAsset: LoanAssetLabel
): Warning[] {
  return warnings
    .map(warning => {
      const message = marketWarningMessage(warning, loanAsset)
      if (!isDefined(message)) return undefined
      return { level: toWarningLevel(warning.level), message, warningType: warning.type }
    })
    .filter(isDefined)
}

/** A row of a market Core does not know has nothing to label it with, so it is left out. */
export function toBorrowActivityRow(tx: UserBorrowTransaction): BorrowActivityTableRow | undefined {
  const market = tx.market
  if (!market) return undefined

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const chainId = tx.chainId as ChainId
  const loanAsset = toInterfaceInputToken({ ...market.loanAsset, priceUsd: null }, chainId)
  const collateralAsset = market.collateralAsset
    ? toInterfaceInputToken({ ...market.collateralAsset, priceUsd: null }, chainId)
    : null
  const tokenAsset = COLLATERAL_TYPES.has(tx.type) ? collateralAsset : loanAsset

  return {
    chainId,
    timestamp: BigInt(tx.timestamp),
    type: MARKET_TRANSACTION_TYPE_TO_LEGACY[tx.type],
    typeText: MARKET_TRANSACTION_TYPE_LABELS[tx.type],
    amount: BigInt(tx.assets),
    usdValue: getParsedValue(tx.assetsUsd, safeParseNumber),
    token: tokenAsset ? parseApiAsset(withRowPrice(tokenAsset, tx.assets, tx.assetsUsd)) : null,
    collateralAsset,
    loanAsset,
    lltv: BigInt(market.lltv),
    warnings: mapMarketActivityWarnings(market.warnings, market.loanAsset),
    hash: tx.txHash,
    urlHash: ChainUtils.getExplorerTransactionUrl(chainId, tx.txHash),
    // oxlint-disable-next-line typescript/consistent-type-assertions
    marketId: tx.marketId as MarketId
  }
}

/** A row of a vault Core does not know has nothing to label it with, so it is left out. */
export function toEarnActivityRow(tx: UserEarnTransaction): EarnActivityTableRow | undefined {
  const vault = tx.vault
  if (!vault) return undefined

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const chainId = tx.chainId as ChainId
  const filterType = EARN_TYPES[tx.type]

  return {
    chainId,
    timestamp: BigInt(tx.timestamp),
    amount: getParsedValue(tx.assets, BigInt) ?? undefined,
    amountUsd: tx.assetsUsd,
    asset: vault.asset
      ? parseApiAsset(
          withRowPrice(
            toInterfaceInputToken({ ...vault.asset, priceUsd: null }, chainId),
            tx.assets,
            tx.assetsUsd
          )
        )
      : null,
    transactionHash: tx.txHash,
    type: filterType,
    typeText: filterType,
    filterType,
    isVaultV2: tx.vaultKind === 'vaultV2',
    urlHash: ChainUtils.getExplorerTransactionUrl(chainId, tx.txHash),
    vaultAddress: vault.address,
    vaultName: vault.name,
    vaultWarnings: mapEarnActivityWarnings(vault.warnings, tx.vaultKind)
  }
}

function liquidationBadDebtWarning(
  market: NonNullable<UserLiquidation['market']>
): Warning | undefined {
  const warning = market.unrealizedBadDebtWarning
  if (!warning) return undefined
  return {
    level: toWarningLevel(warning.level),
    message: unrealizedBadDebtMessage(market.loanAsset, warning.badDebtAssets, warning.badDebtUsd),
    warningType: MarketWarningType.BAD_DEBT_UNREALIZED
  }
}

/** A liquidation on a market Core does not know has no assets to show, so it is left out. */
export function toLiquidationActivityRow(
  tx: UserLiquidation
): LiquidationActivityTableRow | undefined {
  const market = tx.market
  if (!market) return undefined

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const chainId = tx.chainId as ChainId

  return {
    chainId,
    loanAsset: parseApiAsset(
      market.loanAsset.priceUsd === null
        ? withRowPrice(
            toInterfaceInputToken(market.loanAsset, chainId),
            tx.repaidAssets,
            tx.repaidAssetsUsd
          )
        : toInterfaceInputToken(market.loanAsset, chainId)
    ),
    collateralAsset: market.collateralAsset
      ? parseApiAsset(
          withRowPrice(
            toInterfaceInputToken({ ...market.collateralAsset, priceUsd: null }, chainId),
            tx.seizedAssets,
            tx.seizedAssetsUsd
          )
        )
      : null,
    collateralSeized: {
      amount: BigInt(tx.seizedAssets),
      amountUsd: getParsedValue(tx.seizedAssetsUsd, safeParseNumber)
    },
    loanRepaid: {
      amount: BigInt(tx.repaidAssets),
      amountUsd: getParsedValue(tx.repaidAssetsUsd, safeParseNumber)
    },
    realizedBadDebt: {
      amount: BigInt(tx.badDebtAssets),
      amountUsd: getParsedValue(tx.badDebtAssetsUsd, safeParseNumber),
      warning: liquidationBadDebtWarning(market)
    },
    date: { timestamp: BigInt(tx.timestamp) },
    liquidated: { address: tx.borrowerAddress },
    liquidator: { address: tx.liquidatorAddress },
    tx: { hash: tx.txHash },
    // oxlint-disable-next-line typescript/consistent-type-assertions
    marketId: tx.marketId as MarketId,
    lltv: BigInt(market.lltv)
  }
}
